import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie';
import { BehaviorSubject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export type SupportedLanguage = 'ko' | 'en' | 'ja';

export const SUPPORTED_LANGUAGES: SupportedLanguage[] = ['ko', 'en', 'ja'];

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  ko: '한국어',
  en: 'English',
  ja: '日本語',
};

const LANGUAGE_COOKIE_KEY = 'lang';

/**
 * 쿠키 도메인 추출 (서브도메인 공유용)
 * 예: https://nonunbub.com → .nonunbub.com
 * 예: http://localhost:4200 → undefined (localhost는 도메인 설정 불필요)
 */
function getCookieDomain(): string | undefined {
  try {
    const url = new URL(environment.webDomain);
    const hostname = url.hostname;

    // localhost는 도메인 설정 불필요
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return undefined;
    }

    // 최상위 도메인 추출 (서브도메인 공유용)
    // 예: nonunbub.com → .nonunbub.com (api.nonunbub.com에서도 읽기 가능)
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      return '.' + parts.slice(-2).join('.');
    }
    return undefined;
  } catch {
    return undefined;
  }
}

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private currentLanguageSubject = new BehaviorSubject<SupportedLanguage>('ko');
  currentLanguage$: Observable<SupportedLanguage> = this.currentLanguageSubject.asObservable();

  constructor(
    private translate: TranslateService,
    private cookieService: CookieService,
    private route: ActivatedRoute,
  ) {
    this.initializeLanguage();
  }

  /**
   * 언어 초기화
   * 우선순위: URL 쿼리 파라미터 > 쿠키 저장값 > 브라우저 언어 > 기본값(ko)
   *
   * NestJS @Language() 데코레이터와 동일한 우선순위:
   * 1. Query parameter: ?lang=en
   * 2. Cookie: lang=en
   * 3. Browser language (Accept-Language equivalent)
   * 4. Default: ko
   */
  private initializeLanguage(): void {
    // 지원 언어 설정
    this.translate.addLangs(SUPPORTED_LANGUAGES);
    this.translate.setDefaultLang('ko');

    // HOTFIX: 언어 쿠키 강제 설정 (언어 선택 UI 제거로 인한 임시 조치)
    // 일부 사용자의 브라우저 언어가 한국어가 아닌 경우 대응
    // TODO: 향후 언어 선택 기능 재활성화 시 이 코드 제거
    this.setLanguage('ko', true);
    return;

    // 1. URL 쿼리 파라미터 확인 (?lang=en)
    this.route.queryParams.pipe(take(1)).subscribe((params) => {
      const queryLang = params['lang'] as SupportedLanguage;
      if (queryLang && SUPPORTED_LANGUAGES.includes(queryLang)) {
        this.setLanguage(queryLang);
        return;
      }

      // 2. 저장된 쿠키 확인
      const savedLang = this.cookieService.get(LANGUAGE_COOKIE_KEY) as SupportedLanguage;
      if (savedLang && SUPPORTED_LANGUAGES.includes(savedLang)) {
        this.setLanguage(savedLang);
        return;
      }

      // 3. 브라우저 언어 감지
      const browserLang = this.translate.getBrowserLang() as SupportedLanguage;
      if (browserLang && SUPPORTED_LANGUAGES.includes(browserLang)) {
        this.setLanguage(browserLang);
        return;
      }

      // 4. 기본값 사용
      this.setLanguage('ko');
    });
  }

  /**
   * URL에서 언어 파라미터 확인 및 적용
   * 페이지 이동 시 호출하여 ?lang= 파라미터 처리
   */
  checkUrlLanguage(): void {
    this.route.queryParams.pipe(take(1)).subscribe((params) => {
      const queryLang = params['lang'] as SupportedLanguage;
      if (queryLang && SUPPORTED_LANGUAGES.includes(queryLang)) {
        this.setLanguage(queryLang);
      }
    });
  }

  /**
   * 현재 언어 코드 반환
   */
  get currentLanguage(): SupportedLanguage {
    return this.currentLanguageSubject.value;
  }

  /**
   * 언어 변경
   *
   * 쿠키 정책:
   * - 브라우저 언어와 다른 언어 선택 시: 쿠키에 저장 (사용자 선호 유지)
   * - 브라우저 언어와 동일한 언어 선택 시: 쿠키 삭제 (브라우저 설정 변경 자동 반영)
   */
  setLanguage(lang: SupportedLanguage, forced: boolean = false): void {
    if (!SUPPORTED_LANGUAGES.includes(lang) && !forced) {
      console.warn(`Unsupported language: ${lang}`);
      return;
    }

    // 현재 언어와 동일하면 스킵
    if (this.currentLanguageSubject.value === lang && !forced) {
      return;
    }

    this.translate.use(lang);
    this.currentLanguageSubject.next(lang);

    const cookieDomain = getCookieDomain();
    const browserLang = this.translate.getBrowserLang();

    if (browserLang === lang && !forced) {
      // 브라우저 언어와 동일하면 쿠키 삭제
      // → 브라우저 언어 설정 변경 시 자동 반영됨
      this.cookieService.remove(LANGUAGE_COOKIE_KEY, {
        path: '/',
        ...(cookieDomain && { domain: cookieDomain }),
      });
    } else {
      // 브라우저 언어와 다르면 쿠키에 저장 (1년)
      // domain 설정으로 서브도메인(api.nonunbub.com)에서도 읽기 가능
      // sameSite: 'lax'로 cross-site 요청에서도 쿠키 전송 허용
      this.cookieService.put(LANGUAGE_COOKIE_KEY, lang, {
        expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
        path: '/',
        sameSite: 'lax',
        ...(cookieDomain && { domain: cookieDomain }),
      });
    }

    // HTML lang 속성 업데이트 (SEO 및 접근성)
    document.documentElement.lang = lang;
  }

  /**
   * 지원 언어 목록 반환
   */
  getSupportedLanguages(): { code: SupportedLanguage; label: string }[] {
    return SUPPORTED_LANGUAGES.map((code) => ({
      code,
      label: LANGUAGE_LABELS[code],
    }));
  }

  /**
   * 번역 키로 번역된 텍스트 가져오기 (동기)
   */
  instant(key: string, params?: object): string {
    return this.translate.instant(key, params);
  }

  /**
   * 번역 키로 번역된 텍스트 가져오기 (비동기)
   */
  get(key: string, params?: object): Observable<string> {
    return this.translate.get(key, params);
  }
}
